import { useCallback, useState } from 'react';
import type { DecisionDetail } from '@hector/shared-types';
import {
  renderDecisionPdf,
  decisionPdfFileName,
  decisionPieceName,
  decisionPieceDescription,
  formatDecisionTitle,
} from '@hector/decision-document';
import { toast } from 'sonner';
import { ErrorService } from '@/lib/errors/ErrorService';
import { storageService } from '@/services/storageService';
import { apiClient } from '@/lib/apiClient';
import { useBatchSynthesis } from '@/hooks/useBatchSynthesis';
import { useAllCases } from '@/hooks/useCases';
import { useQueryClient } from '@tanstack/react-query';
import { DOCUMENT_CATEGORIES } from '@/data/documentCategories';
import { SaveToDestinationDialog } from '@/components/documents/destination/SaveToDestinationDialog';
import type { DestinationValue } from '@/components/documents/destination/destinationModel';
import type { CaseMetadata } from '@/services/folderHierarchyService';

/**
 * Enregistre une décision du corpus comme pièce d'un dossier.
 *
 * Le CHOIX de la destination — dossier, onglet, sous-dossier — vit dans
 * `SaveToDestinationDialog`, partagé avec les autres écrans d'enregistrement.
 * Ce module ne garde que ce qui lui est propre : produire le PDF et écrire.
 *
 * ─── Décisions de conception ──────────────────────────────────────────────
 *
 * **Le lien de retour vit dans le PDF, pas dans l'interface.** Le geste courant
 * sur une pièce est le double-clic, qui ouvre le fichier ; une action rangée
 * dans un menu contextuel ne serait presque jamais découverte. `renderDecisionPdf`
 * imprime donc, sous la citation, la source officielle et un lien vers le viewer
 * Hector. Le lien survit au téléchargement comme au transfert du fichier.
 *
 * **On ne crée aucune exception dans l'onglet.** Tout fichier déposé dans
 * « Mes recherches juridiques » passe par le traitement IA (`submitBatches`) —
 * une décision enregistrée ici doit s'y comporter comme un PDF déposé à la main,
 * sinon elle serait la seule pièce non indexée, invisible en recherche sémantique
 * et sans badge d'état.
 *
 * **`generateTitle: false`** préserve le titre canonique (juridiction, date,
 * numéro) : ce sont les seuls termes par lesquels l'agent retrouve la décision
 * tant que la vectorisation n'est pas finie.
 */

/**
 * Onglets proposés. `partie_adverse` est exclue : elle exige une sous-catégorie
 * structurante sans laquelle la pièce n'est navigable nulle part, et une décision
 * du corpus n'est pas une écriture adverse. Pour l'y ranger malgré tout, l'avocat
 * déplace la pièce ensuite via « Déplacer vers », qui gère la règle.
 */
const SAVE_TARGET_CATEGORIES = [
  'recherche',
  'nos_pieces',
  'procedures',
  'procedure',
  'notes_projets',
] as const;

export function SaveDecisionDialog({
  open,
  onOpenChange,
  decision,
  preselectedCaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  decision: DecisionDetail | null;
  /** Dossier actif, pré-sélectionné quand il y en a un. */
  preselectedCaseId?: string;
}) {
  const queryClient = useQueryClient();
  const { data: cases = [] } = useAllCases();
  // Le dossier RÉELLEMENT choisi, pas le présélectionné : `useBatchSynthesis`
  // scope ses invalidations et les métadonnées du job dessus.
  const [destination, setDestination] = useState<DestinationValue | null>(null);
  const { submitBatches } = useBatchSynthesis(destination?.caseId ?? preselectedCaseId ?? '');
  const handleSelectionChange = useCallback((value: DestinationValue) => setDestination(value), []);

  const handleConfirm = async ({ destination }: { destination: DestinationValue }) => {
    if (!decision) return;
    const { caseId, category, folderPath } = destination;
    let storagePath = '';

    try {
      // `window.location.origin` : le lien « Ouvrir dans Hector » imprimé dans le
      // PDF pointe vers l'environnement courant (prod, staging ou local).
      const pdf = renderDecisionPdf(decision, window.location.origin);
      const blob = pdf.output('blob') as Blob;

      const documentId = crypto.randomUUID();
      const file = new File([blob], decisionPdfFileName(decision), {
        type: 'application/pdf',
        lastModified: Date.now(),
      });

      const uploadResult = await storageService.uploadFile(file, { caseId, category });
      storagePath = (Array.isArray(uploadResult) ? uploadResult[0]?.path : uploadResult.path) ?? '';

      const pieceName = decisionPieceName(decision);

      try {
        await apiClient.post(`/cases/${caseId}/documents`, {
          id: documentId,
          name: pieceName,
          file_path: storagePath,
          document_type: 'application/pdf',
          document_category: category,
          // Nature déterministe : c'est une décision de justice, on le sait de
          // source sûre. Posée ici, l'auto-classification post-OCR la respecte
          // (l'étape est idempotente). Hors `recherche`, la sous-catégorie est un
          // attribut libre que rien ne lit — on ne l'écrit pas.
          document_subcategory: category === 'recherche' ? 'decision' : null,
          folder_path: folderPath,
          folder_depth: folderPath.length,
          parent_folder_path: folderPath.slice(0, -1),
          display_folder_path: folderPath.length > 0 ? folderPath.join('/') : null,
          is_folder: false,
          file_size: file.size,
          piece_description: decisionPieceDescription(decision),
        });
      } catch (insertError) {
        ErrorService.handle(insertError, {
          context: { action: 'save_decision_insert', case_id: caseId, document_id: documentId },
          silent: true,
        });
        // L'objet S3 est déjà écrit : sans ce retrait, il resterait orphelin,
        // facturé et invisible (aucune ligne ne le référence).
        await storageService.removeFiles(storagePath);
        throw insertError;
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['folder-tree', caseId, category] }),
        queryClient.invalidateQueries({ queryKey: ['folder-contents', caseId, category] }),
        queryClient.invalidateQueries({ queryKey: ['case-documents', caseId, category] }),
        queryClient.invalidateQueries({ queryKey: ['case-documents'] }),
      ]);

      // Traitement IA — identique à un dépôt manuel (cf. docblock du module).
      const selectedCase = cases.find((c) => c.id === caseId);
      if (selectedCase) {
        const metadata: CaseMetadata = {
          dossierID: caseId,
          dossierName: selectedCase.title || 'Dossier sans titre',
          clientName: selectedCase.client_name || 'Client non spécifié',
          lawyerName: selectedCase.assigned_to || 'Non assigné',
          typeDeProcedure: selectedCase.procedure_type || 'Type non spécifié',
        };
        submitBatches([{ documentId, fileName: pieceName }], metadata, {
          category,
          generateTitle: false,
        }).catch((err) => {
          ErrorService.handle(err, {
            context: { action: 'save_decision_synthesis', case_id: caseId, document_id: documentId },
            silent: true,
          });
        });
      }

      toast.success('Décision enregistrée', {
        description: `Disponible dans « ${DOCUMENT_CATEGORIES[category]?.label ?? category} »`,
      });
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'save_decision', case_id: caseId } });
      // Relancé : le dialogue partagé garde alors la fenêtre ouverte, choix intacts.
      throw error;
    }
  };

  return (
    <SaveToDestinationDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Enregistrer la décision"
      description={decision ? formatDecisionTitle(decision) : ''}
      confirmLabel="Enregistrer"
      initialCaseId={preselectedCaseId}
      defaultCategory="recherche"
      categories={SAVE_TARGET_CATEGORIES}
      onSelectionChange={handleSelectionChange}
      onConfirm={handleConfirm}
    />
  );
}
